import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import type { Notification } from '@/types/notifications';
import { NotificationCard } from './NotificationCard';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface NotificationGroupProps {
  groupKey: string;
  notifications: Notification[];
  appName: string;
  appIcon?: string;
  onMarkAsRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onSnooze?: (id: string, minutes: number) => void;
  onActionClick?: (notificationId: string, actionId: string) => void;
  onMarkAllAsRead?: (groupKey: string) => void;
}

/**
 * Collapsible group container for related notifications
 * Features: Count badge, expandable, smooth animations, group actions
 */
export const NotificationGroup: React.FC<NotificationGroupProps> = ({
  groupKey,
  notifications,
  appName,
  appIcon,
  onMarkAsRead,
  onDismiss,
  onSnooze,
  onActionClick,
  onMarkAllAsRead,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get group statistics
  const count = notifications.length;
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const latestNotification = notifications[0]; // Assuming sorted by created_at desc

  // Get app icon component
  const getAppIcon = () => {
    const iconName = appIcon || latestNotification.app_icon || 'Bell';
    const IconComponent = (Icons as any)[iconName] || Icons.Bell;
    return <IconComponent className="w-5 h-5" />;
  };

  // Get relative time for latest notification
  const timeAgo = formatDistanceToNow(new Date(latestNotification.created_at), { addSuffix: true });

  // Get priority-based styling (use highest priority in group)
  const getHighestPriority = () => {
    const priorities = ['urgent', 'high', 'default', 'low', 'min'];
    for (const priority of priorities) {
      if (notifications.some(n => n.priority === priority)) {
        return priority;
      }
    }
    return 'default';
  };

  const highestPriority = getHighestPriority();

  const getPriorityStyles = () => {
    switch (highestPriority) {
      case 'urgent':
        return {
          border: 'border-l-red-500',
          glow: 'shadow-[0_0_15px_rgba(239,68,68,0.3)]',
          badge: 'bg-red-500 text-white',
        };
      case 'high':
        return {
          border: 'border-l-orange-500',
          glow: 'shadow-[0_0_15px_rgba(249,115,22,0.3)]',
          badge: 'bg-orange-500 text-white',
        };
      default:
        return {
          border: 'border-l-blue-500',
          glow: 'shadow-[0_0_12px_rgba(59,130,246,0.25)]',
          badge: 'bg-blue-500 text-white',
        };
    }
  };

  const priorityStyles = getPriorityStyles();

  // Handle expand/collapse
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAllAsRead?.(groupKey);
  };

  return (
    <div className="space-y-2">
      {/* Group Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.01 }}
        className={cn(
          'group relative rounded-xl p-4 border-l-4 transition-all duration-200 cursor-pointer',
          priorityStyles.border,
          priorityStyles.glow,
          unreadCount > 0 && 'bg-white/5',
          unreadCount === 0 && 'bg-white/[0.02] opacity-70',
          // Glassmorphic effect
          'backdrop-blur-xl',
          'border border-white/10',
          'hover:bg-white/8 hover:border-white/20'
        )}
        onClick={toggleExpanded}
      >
        {/* Unread count badge */}
        {unreadCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={cn(
              'absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-bold',
              priorityStyles.badge
            )}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.div>
        )}

        <div className="flex items-start gap-3">
          {/* App Icon */}
          <div className="flex-shrink-0 relative">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-white/10 to-white/5 border border-white/20 flex items-center justify-center text-white/70">
              {getAppIcon()}
            </div>

            {/* Notification count overlay */}
            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-white/20 flex items-center justify-center">
              <span className="text-[10px] font-bold text-white">
                {count > 99 ? '99+' : count}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-white">
                  {count} notification{count !== 1 ? 's' : ''} from {appName}
                </h4>
                {isExpanded ? (
                  <Icons.ChevronUp className="w-4 h-4 text-white/50" />
                ) : (
                  <Icons.ChevronDown className="w-4 h-4 text-white/50" />
                )}
              </div>
              
              <span className="text-xs text-white/50 whitespace-nowrap">
                {timeAgo}
              </span>
            </div>

            {/* Preview (latest notification) */}
            <p className="text-sm text-white/70 mb-2 leading-relaxed line-clamp-1">
              {latestNotification.title}
            </p>

            {/* Metadata */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs text-white/50">
                <span>Latest: {timeAgo}</span>
                {highestPriority && highestPriority !== 'default' && (
                  <>
                    <span>•</span>
                    <span className="capitalize">{highestPriority} priority</span>
                  </>
                )}
              </div>

              {/* Quick Actions */}
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      </motion.div>

      {/* Expandable Notifications List */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="space-y-2 overflow-hidden"
          >
            {notifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <NotificationCard
                  notification={notification}
                  onMarkAsRead={onMarkAsRead}
                  onDismiss={onDismiss}
                  onSnooze={onSnooze}
                  onActionClick={onActionClick}
                  isGrouped
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
